import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { IChannel } from '@/entities/channel';
import { BillingDialog, ChannelSubscription } from '@/features/billing';
import {
  ChannelIconWithLabel,
  ChannelsList,
  getPersonalChannelPhoneOverride,
  useDeleteChannel,
} from '@/features/channels';
import {
  useChannelsOfCompanyList,
  useDeleteChannelFromCompany,
} from '@/features/company';
import { Notification } from '@/shared/ui';
import { Loader } from '@/shared/ui/Loader';

import { ActionsButton } from './ActionsButton';

export const ChannelsOfCompanyTab = () => {
  const { id: companyId } = useParams<{ id: string }>();
  const { deleteChannel } = useDeleteChannel();

  const { channelsOfCompanyData, isPending, isError, error } =
    useChannelsOfCompanyList(companyId);

  const { deleteChannelFromCompany } = useDeleteChannelFromCompany();
  const [billingChannel, setBillingChannel] = useState<IChannel | null>(null);

  if (isPending) {
    return <Loader containerSize="medium" />;
  }

  if (isError) {
    return (
      <Notification open={isError} title={error?.message}>
        {error?.data?.details}
      </Notification>
    );
  }

  return (
    <>
      <ChannelsList
        channels={channelsOfCompanyData}
        isPending={isPending}
        renderActions={(channel: IChannel) => (
          <ActionsButton
            channel={channel}
            companyId={companyId}
            onDelete={deleteChannel}
            onUnlink={deleteChannelFromCompany}
          />
        )}
        renderTariff={(channel: IChannel) => (
          <ChannelSubscription
            channelId={channel.id}
            phoneOverride={getPersonalChannelPhoneOverride(channel.protocol, channel.phone_number)}
          />
        )}
        onPaySubscription={setBillingChannel}
        onRenewSubscription={setBillingChannel}
      />

      <BillingDialog
        key={billingChannel?.id}
        open={Boolean(billingChannel)}
        onClose={() => setBillingChannel(null)}
        onBack={() => setBillingChannel(null)}
        channelProtocol={billingChannel?.protocol}
        isRenewing
        channelId={billingChannel?.id}
        renderIconWithLabel={(renderLabelByProtocol) =>
          billingChannel ? (
            <ChannelIconWithLabel
              label={renderLabelByProtocol(billingChannel.protocol)}
              protocol={billingChannel.protocol}
            />
          ) : null
        }
      />
    </>
  );
};
